import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Download.css';

/**
 * Download Landing Page Component
 * 
 * This component serves as the main landing page for the On-Screen PWA.
 * It provides:
 * - App information and features
 * - PWA installation functionality with platform detection
 * - User guide access
 * - Responsive design for mobile and desktop
 * 
 * Color scheme:
 * - Primary Orange: #ff6800
 * - Primary Blue: #01057a
 * - Gray: #3C3C3C
 */
function Download() {
  const navigate = useNavigate();
  
  // State for PWA installation
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [platform, setPlatform] = useState('unknown');
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  
  /**
   * Detect user's platform on component mount
   * Determines if user is on iOS, Android, or Desktop
   */
  useEffect(() => {
    detectPlatform();
    checkIfInstalled();
    
    // Listen for the beforeinstallprompt event (Chrome, Edge, Samsung Internet)
    const handleBeforeInstallPrompt = (e) => {
      console.log('📱 PWA install prompt available');
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
    };
    
    // Listen for successful installation
    const handleAppInstalled = () => {
      console.log('✅ PWA was installed');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Cleanup event listeners
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);
  
  /**
   * Detect the user's platform
   * Sets the platform state to 'ios', 'android', 'desktop', or 'unknown'
   */
  const detectPlatform = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // iOS detection
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setPlatform('ios');
      return;
    }
    
    // Android detection
    if (/android/i.test(userAgent)) {
      setPlatform('android');
      return;
    }
    
    // Desktop detection
    if (!/Mobi|Android/i.test(userAgent)) {
      setPlatform('desktop');
      return;
    }
    
    setPlatform('unknown');
  };
  
  /**
   * Check if the PWA is already installed
   * Uses display-mode media query to detect standalone mode
   */
  const checkIfInstalled = () => {
    // Check if running as installed PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isIOSStandalone = window.navigator.standalone === true;
    
    if (isStandalone || (isIOS && isIOSStandalone)) {
      setIsInstalled(true);
      console.log('✅ App is running as installed PWA');
    }
  };
  
  /**
   * Handle PWA installation
   * Triggers the native install prompt for supported browsers
   */
  const handleInstall = async () => {
    if (!deferredPrompt) {
      console.log('ℹ️ No install prompt available');
      
      // For iOS, show manual instructions
      if (platform === 'ios') {
        setShowIOSInstructions(true);
      }
      return;
    }
    
    try {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      console.log(`👤 User response to install prompt: ${outcome}`);
      
      if (outcome === 'accepted') {
        console.log('✅ User accepted the install prompt');
        setIsInstalled(true);
      } else {
        console.log('❌ User dismissed the install prompt');
      }
      
      // Clear the deferredPrompt for next time
      setDeferredPrompt(null);
      setIsInstallable(false);
      
    } catch (error) {
      console.error('❌ Error during PWA installation:', error);
    }
  };
  
  /**
   * Navigate to login page
   */
  const handleGetStarted = () => {
    navigate('/login');
  };
  
  /**
   * Navigate to owner signup page
   */
  const handleOwnerSignup = () => {
    navigate('/owneradd');
  };
  
  /**
   * Render platform-specific installation instructions
   */
  const renderInstallInstructions = () => {
    if (isInstalled) {
      return (
        <div className="install-status installed">
          <div className="status-icon">✓</div>
          <h3>App Installed Successfully!</h3>
          <p>You can now access On-Screen from your home screen.</p>
          <button className="btn btn-primary" onClick={handleGetStarted}>
            Get Started
          </button>
        </div>
      );
    }
    
    // Chrome/Edge/Samsung Internet - Native install prompt available
    if (isInstallable && platform !== 'ios') {
      return (
        <div className="install-actions">
          <button className="btn btn-install" onClick={handleInstall}>
            <span className="install-icon">📥</span>
            Install App
          </button>
          <button className="btn btn-secondary" onClick={handleGetStarted}>
            Use in Browser
          </button>
        </div>
      );
    }
    
    // iOS - Manual installation required
    if (platform === 'ios') {
      return (
        <div className="install-instructions">
          <button 
            className="btn btn-install" 
            onClick={() => setShowIOSInstructions(!showIOSInstructions)}
          >
            <span className="install-icon">📥</span>
            Install on iOS
          </button>
          
          {showIOSInstructions && (
            <div className="ios-instructions">
              <h4>To install On-Screen on iOS:</h4>
              <ol>
                <li>Tap the <strong>Share</strong> button <span className="ios-icon">⎙</span> in Safari</li>
                <li>Scroll down and tap <strong>"Add to Home Screen"</strong> <span className="ios-icon">➕</span></li>
                <li>Tap <strong>"Add"</strong> in the top right</li>
                <li>Find the On-Screen icon on your home screen</li>
              </ol>
            </div>
          )}
          
          <button className="btn btn-secondary" onClick={handleGetStarted}>
            Use in Browser
          </button>
        </div>
      );
    }
    
    // Android/Desktop - Fallback for browsers without install prompt
    return (
      <div className="install-instructions">
        <p className="install-note">
          To install this app, look for the "Install" option in your browser menu.
        </p>
        <button className="btn btn-primary" onClick={handleGetStarted}>
          Continue in Browser
        </button>
      </div>
    );
  };
  
  /**
   * User guide items with descriptions
   */
  const userGuides = [
    {
      title: 'Getting Started',
      description: 'Learn how to set up your account and connect your social media platforms',
      icon: '🚀',
      link: '#getting-started'
    },
    {
      title: 'Scheduling Posts',
      description: 'Master the art of scheduling content across multiple platforms',
      icon: '📅',
      link: '#scheduling'
    },
    {
      title: 'Event Management',
      description: 'Create and manage events with auctions, raffles, and contests',
      icon: '🎉',
      link: '#events'
    },
    {
      title: 'Analytics & Insights',
      description: 'Track your performance and grow your audience',
      icon: '📊',
      link: '#analytics'
    },
    {
      title: 'Integration Setup',
      description: 'Connect Instagram, Facebook, Twitter, LinkedIn, YouTube, TikTok, and Bluesky',
      icon: '🔗',
      link: '#integrations'
    },
    {
      title: 'Billing & Subscriptions',
      description: 'Manage your subscription plan and usage limits',
      icon: '💳',
      link: '#billing'
    }
  ];
  
  /**
   * App features to showcase
   */
  const features = [
    {
      icon: '📱',
      title: 'Multi-Platform Support',
      description: 'Manage Instagram, Facebook, Twitter, LinkedIn, YouTube, TikTok, and Bluesky all in one place'
    },
    {
      icon: '⏰',
      title: 'Smart Scheduling',
      description: 'Schedule posts in advance and publish at optimal times'
    },
    {
      icon: '🎯',
      title: 'Event Management',
      description: 'Create engaging events with auctions, raffles, contests, and more'
    },
    {
      icon: '📈',
      title: 'Analytics Dashboard',
      description: 'Track performance metrics and audience engagement'
    },
    {
      icon: '🎨',
      title: 'Media Editor',
      description: 'Edit images and videos directly in the app'
    },
    {
      icon: '🔔',
      title: 'Push Notifications',
      description: 'Stay updated with real-time notifications'
    }
  ];

  return (
    <div className="download-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                <span className="brand-name">On-Screen</span>
                <span className="hero-subtitle">Social Media Management, Simplified</span>
              </h1>
              <p className="hero-description">
                The all-in-one platform for scheduling posts, managing events, 
                and growing your social media presence across multiple platforms.
              </p>
              
              {/* Installation Section */}
              <div className="install-section">
                {renderInstallInstructions()}
              </div>
              
              {/* Additional CTA for new users */}
              <div className="cta-secondary">
                <p className="cta-text">Don't have an account yet?</p>
                <button className="btn btn-outline" onClick={handleOwnerSignup}>
                  Sign Up Free
                </button>
              </div>
            </div>
            
            {/* Hero Image/Icon */}
            <div className="hero-image">
              <div className="app-preview">
                <div className="app-icon">📱</div>
                <div className="app-screens">
                  <div className="screen screen-1"></div>
                  <div className="screen screen-2"></div>
                  <div className="screen screen-3"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Powerful Features</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* User Guides Section */}
      <section className="guides-section">
        <div className="container">
          <h2 className="section-title">User Guides</h2>
          <p className="section-description">
            Everything you need to know to get the most out of On-Screen
          </p>
          
          <div className="guides-grid">
            {userGuides.map((guide, index) => (
              <a 
                key={index} 
                href={guide.link} 
                className="guide-card"
                onClick={(e) => {
                  e.preventDefault();
                  console.log(`Navigating to guide: ${guide.title}`);
                  // TODO: Implement navigation to guide pages or external documentation
                  // For now, you can link to external documentation or create guide pages
                }}
              >
                <div className="guide-icon">{guide.icon}</div>
                <div className="guide-content">
                  <h3 className="guide-title">{guide.title}</h3>
                  <p className="guide-description">{guide.description}</p>
                </div>
                <div className="guide-arrow">→</div>
              </a>
            ))}
          </div>
          
          {/* Additional Resources */}
          <div className="resources-section">
            <h3 className="resources-title">Need More Help?</h3>
            <div className="resources-links">
              <a href="/termsofuse" className="resource-link">Terms of Use</a>
              <a href="/privacypolicy" className="resource-link">Privacy Policy</a>
              <a href="/sendemail" className="resource-link">Contact Support</a>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-description">
              Join thousands of users managing their social media presence with On-Screen
            </p>
            <div className="cta-buttons">
              {!isInstalled && isInstallable && (
                <button className="btn btn-install-large" onClick={handleInstall}>
                  <span className="install-icon">📥</span>
                  Install App Now
                </button>
              )}
              {!isInstalled && platform === 'ios' && (
                <button 
                  className="btn btn-install-large" 
                  onClick={() => setShowIOSInstructions(true)}
                >
                  <span className="install-icon">📥</span>
                  Install on iOS
                </button>
              )}
              <button className="btn btn-primary-large" onClick={handleGetStarted}>
                Launch App
              </button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="download-footer">
        <div className="container">
          <div className="footer-content">
            <p className="footer-text">
              © 2025 On-Screen. All rights reserved.
            </p>
            <div className="footer-links">
              <a href="/termsofuse" className="footer-link">Terms</a>
              <a href="/privacypolicy" className="footer-link">Privacy</a>
              <a href="/sendemail" className="footer-link">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Download;